import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

/**
 * CsvImportModal - Modal reutilizable para importar archivos CSV.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   title: string (e.g. "Importar Clientes")
 *   description: string (e.g. "Formato: CODIGO_CLIENTE;CODIGO_COMERCIAL;...")
 *   onImport: (file: File) => Promise<{ imported, errors? }>
 *   delimiter: string (default ";")
 */
const CsvImportModal = ({ isOpen, onClose, title, description, onImport, delimiter = ';', extraFields }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview([]);
    setHeaders([]);
    setResult(null);
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const parsePreview = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return;

    const headerRow = lines[0].split(delimiter).map(h => h.trim());
    setHeaders(headerRow);

    const dataRows = lines.slice(1, 6).map(line =>
      line.split(delimiter).map(cell => cell.trim())
    );
    setPreview(dataRows);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      let text = ev.target.result;
      // If garbled, re-read as latin1 is not straightforward in browser,
      // but we can detect and warn
      parsePreview(text);
    };
    reader.readAsText(selectedFile, 'UTF-8');
  };

  const handleImport = async () => {
    if (!file || importing) return;
    setImporting(true);
    setResult(null);

    try {
      const res = await onImport(file);
      setResult(res);
    } catch (err) {
      setResult({ error: true, message: err.message || 'Error desconocido' });
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Upload size={20} style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Extra fields (e.g. cmatriz selector) */}
          {extraFields && extraFields}

          {/* File input */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0099FF] transition-colors">
            <FileText size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-2">Selecciona un archivo CSV o TXT</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileChange}
              className="block mx-auto text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:text-white file:cursor-pointer"
              style={{ maxWidth: 320 }}
            />
            <style>{`input[type="file"]::file-selector-button { background-color: #0099FF; }`}</style>
          </div>

          {/* Preview */}
          {headers.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Vista previa ({preview.length} filas de ejemplo)
              </p>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.map((row, ri) => (
                      <tr key={ri} className="hover:bg-gray-50">
                        {headers.map((_, ci) => (
                          <td key={ci} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">
                            {row[ci] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-lg border ${result.error
              ? 'bg-red-50 border-red-200'
              : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-start gap-2">
                {result.error
                  ? <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                  : <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                }
                <div>
                  <p className={`text-sm font-medium ${result.error ? 'text-red-700' : 'text-green-700'}`}>
                    {result.message}
                  </p>
                  {result.imported !== undefined && (
                    <p className="text-xs text-gray-600 mt-1">
                      {result.imported} registros importados
                      {result.created !== undefined && ` (${result.created} nuevos, ${result.updated} actualizados)`}
                    </p>
                  )}
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <p className="text-xs font-medium text-red-600 mb-1">Errores ({result.errors.length}):</p>
                      <ul className="text-xs text-red-600 space-y-0.5">
                        {result.errors.slice(0, 20).map((e, i) => (
                          <li key={i}>- {e}</li>
                        ))}
                        {result.errors.length > 20 && (
                          <li className="font-medium">... y {result.errors.length - 20} errores más</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cerrar
          </button>
          <button
            onClick={handleImport}
            disabled={!file || importing}
            className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#0099FF' }}
          >
            {importing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Upload size={16} />
                Importar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
